// Keeps a signed-in session alive, or ends it clearly (B-001).
//
// Before the access token expires (2 minutes before, or half its life if that is shorter)
// the keeper swaps the refresh token for a new session. It checks again when the tab comes
// back into view or the network returns (a sleeping laptop's timers fire late). A refresh
// that can't get through is retried every 15 seconds until the token expires.
//
// The session ends — token forgotten, the sign-in page says "Your session ended" and brings
// the person back to where they were — when:
//   * Auth refuses the refresh token (signed out elsewhere, revoked);
//   * the token expires and there is no way to renew it (dev tokens, or still unreachable);
//   * the API answers 401 to the stored token and a refresh doesn't fix it (`recover`, used
//     by the API client, which then retries the request once with the new token).
// It never ends silently: every API call after expiry used to fail one by one.

import type { AuthProvider } from './providers.js';
import { getRefreshToken, getToken, onTokenChange, setSession, tokenTimes } from './token-store.js';

export const REFRESH_MARGIN_MS = 2 * 60_000;
export const RETRY_MS = 15_000;

export interface KeeperDeps {
  auth: Pick<AuthProvider, 'refresh'>;
  /** Called once the session has ended (after the token is forgotten). */
  onSessionEnded: () => void;
  now?: () => number;
  setTimer?: (fn: () => void, ms: number) => unknown;
  clearTimer?: (handle: unknown) => void;
}

export class SessionKeeper {
  private timer: unknown = null;
  private inflight: Promise<string | null> | null = null;
  private readonly now: () => number;
  private readonly setTimer: (fn: () => void, ms: number) => unknown;
  private readonly clearTimer: (handle: unknown) => void;
  private stopListening: (() => void) | null = null;

  constructor(private readonly deps: KeeperDeps) {
    this.now = deps.now ?? (() => Date.now());
    this.setTimer = deps.setTimer ?? ((fn, ms) => setTimeout(fn, ms));
    this.clearTimer = deps.clearTimer ?? ((h) => clearTimeout(h as ReturnType<typeof setTimeout>));
  }

  /** Starts watching the stored session. Returns a stop function. */
  start(): () => void {
    const unsubscribe = onTokenChange(() => this.schedule());
    const wake = () => {
      if (typeof document === 'undefined' || document.visibilityState !== 'hidden') {
        void this.check();
      }
    };
    if (typeof window !== 'undefined') {
      document.addEventListener('visibilitychange', wake);
      window.addEventListener('online', wake);
      window.addEventListener('focus', wake);
    }
    this.stopListening = () => {
      unsubscribe();
      if (typeof window !== 'undefined') {
        document.removeEventListener('visibilitychange', wake);
        window.removeEventListener('online', wake);
        window.removeEventListener('focus', wake);
      }
    };
    void this.check();
    return () => this.stop();
  }

  stop(): void {
    this.cancelTimer();
    this.stopListening?.();
    this.stopListening = null;
  }

  /** When the current token should be renewed (ms), or null if it can't be told. */
  private renewAt(token: string): { renewAt: number; expiresAt: number } | null {
    const times = tokenTimes(token);
    if (!times) return null;
    const life = times.issuedAt === null ? Infinity : times.expiresAt - times.issuedAt;
    const margin = Math.min(REFRESH_MARGIN_MS, life / 2);
    return { renewAt: times.expiresAt - margin, expiresAt: times.expiresAt };
  }

  private cancelTimer() {
    if (this.timer !== null) this.clearTimer(this.timer);
    this.timer = null;
  }

  private schedule(delayMs?: number) {
    this.cancelTimer();
    const token = getToken();
    if (!token) return;
    const when = this.renewAt(token);
    if (!when) return; // Not a JWT we can read: the API's 401 decides (see recover).
    const delay = delayMs ?? Math.max(0, when.renewAt - this.now());
    // Timers can't wait longer than ~24.8 days; re-check well before that.
    this.timer = this.setTimer(() => void this.check(), Math.min(delay, 6 * 60 * 60_000));
  }

  /** Renews the session if it's due, ends it if it has expired, otherwise waits. */
  async check(): Promise<void> {
    const token = getToken();
    if (!token) return this.cancelTimer();
    const when = this.renewAt(token);
    if (!when) return;
    if (this.now() < when.renewAt) return this.schedule();
    await this.renew(false);
  }

  /**
   * The API refused a token (401): renew now if possible, else end the session. Returns the
   * new token, or null when the session has ended. `refusedToken` is the token the request
   * went out with: when the stored token is already a different one (renewed, or signed in
   * again, while the request was in flight), that one is returned instead of refreshing again
   * (CR8B-27): a stale 401 would otherwise end a session that has no refresh token.
   */
  recover(refusedToken?: string): Promise<string | null> {
    const current = getToken();
    if (refusedToken !== undefined && current !== null && current !== refusedToken) {
      return Promise.resolve(current);
    }
    return this.renew(true);
  }

  private renew(refused: boolean): Promise<string | null> {
    this.inflight ??= this.doRenew(refused).finally(() => {
      this.inflight = null;
    });
    return this.inflight;
  }

  private async doRenew(refused: boolean): Promise<string | null> {
    const token = getToken();
    if (!token) return null;
    const when = this.renewAt(token);
    const expired = refused || (when !== null && this.now() >= when.expiresAt);
    const refreshToken = getRefreshToken();
    if (!refreshToken || !this.deps.auth.refresh) {
      if (expired) this.end();
      else this.schedule(Math.max(0, (when?.expiresAt ?? this.now()) - this.now()));
      return null;
    }
    const result = await this.deps.auth.refresh(refreshToken);
    if (getToken() !== token) return getToken(); // Signed out or in again meanwhile.
    if (result.status === 'ok') {
      setSession(result.session); // Listeners reschedule for the new token.
      return result.session.token;
    }
    if (result.status === 'rejected' || expired) {
      this.end();
      return null;
    }
    this.schedule(RETRY_MS);
    return null;
  }

  private end() {
    this.cancelTimer();
    setSession(null);
    this.deps.onSessionEnded();
  }
}
